import { useEffect, useRef } from "react";

function isBrowserRuntime(): boolean {
  return typeof window !== "undefined" && typeof document !== "undefined";
}

function isElementFullyVisible(el: HTMLElement): boolean {
  const rect = el.getBoundingClientRect();
  const viewportHeight = window.innerHeight || document.documentElement?.clientHeight || 0;
  const viewportWidth = window.innerWidth || document.documentElement?.clientWidth || 0;

  return (
    rect.top >= 0 &&
    rect.left >= 0 &&
    rect.bottom <= viewportHeight &&
    rect.right <= viewportWidth
  );
}

function focusElement(el: HTMLElement): void {
  if (!el.hasAttribute("tabindex")) {
    el.setAttribute("tabindex", "-1");
  }

  if (typeof el.focus === "function") {
    try {
      el.focus({ preventScroll: true });
    } catch {
      el.focus();
    }
  }
}

/**
 * Scrolls the referenced element into view (centered, smooth) if it is not fully visible in the viewport.
 * @param ref - React ref to the element
 * @param trigger - Boolean or dependency array to trigger the scroll
 */
export function useScrollIntoViewIfNotVisible(ref: React.RefObject<HTMLElement>, trigger: boolean) {
  const prevTriggerRef = useRef(false);

  useEffect(() => {
    const el = ref.current;

    if (!isBrowserRuntime() || !el) {
      prevTriggerRef.current = trigger;
      return;
    }

    // Only run when trigger transitions from false to true
    if (trigger && !prevTriggerRef.current) {
      if (!isElementFullyVisible(el)) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
      }

      focusElement(el);
    }

    prevTriggerRef.current = trigger;
  }, [trigger, ref]);
}
